import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppRole, checkRole, checkAnyRole } from '@/utils/permissions';
import { notifyError } from '@/utils/notify';
import { useBootstrapUsuario, BOOTSTRAP_QUERY_KEY } from '@/hooks/useBootstrapUsuario';

export type { AppRole };

interface ProfileData {
  id: string;
  email: string;
  telefone: string | null;
  avatar_url: string | null;
  display_name: string | null;
  tenant_id: string | null;
  departamento_id: string | null;
  supervisor_id: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: ProfileData | null;
  roles: AppRole[];
  loading: boolean;
  tenantId: string | null;
  rolesLoading: boolean;
  rolesError: boolean;
  podeAcessar: boolean;
  hasRole: (role: AppRole) => boolean;
  hasAnyRole: (roles: AppRole[]) => boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const locationRef = useRef(location.pathname);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Mantém ref atualizada sem causar re-criação da subscription
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  // Bootstrap unificado (roles + perfil + branding + menu + pode_acessar)
  const bootstrapQuery = useBootstrapUsuario(user?.id);
  const bootstrap = bootstrapQuery.data;

  // Derivados do bootstrap (com fallback para metadados do JWT)
  const roles: AppRole[] = bootstrap?.roles ?? [];
  const tenantId: string | null = useMemo(() => {
    if (bootstrap?.tenant_id) return bootstrap.tenant_id;
    const fromJwt = user?.app_metadata?.tenant_id;
    return typeof fromJwt === 'string' ? fromJwt : null;
  }, [bootstrap?.tenant_id, user?.app_metadata?.tenant_id]);

  const profile: ProfileData | null = useMemo(() => {
    const p = bootstrap?.perfil;
    if (!p) return null;
    return {
      id: p.id,
      email: p.email,
      telefone: p.telefone,
      avatar_url: p.avatar_url,
      display_name: p.display_name || (user?.user_metadata?.display_name as string | undefined) || null,
      tenant_id: p.tenant_id ?? tenantId,
      departamento_id: p.departamento_id,
      supervisor_id: p.supervisor_id,
    };
  }, [bootstrap?.perfil, user?.user_metadata?.display_name, tenantId]);

  const rolesLoading = !!user && bootstrapQuery.isLoading;
  const rolesError = !!user && bootstrapQuery.isError;
  const podeAcessar = bootstrap?.pode_acessar ?? true;

  const refreshProfile = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: BOOTSTRAP_QUERY_KEY });
  }, [queryClient]);

  // Bloqueio de usuário inativo + tenant guard, executados quando bootstrap chega
  useEffect(() => {
    if (!bootstrap || !user) return;
    if (bootstrap.perfil && bootstrap.perfil.ativo === false) {
      void supabase.auth.signOut();
      return;
    }
    const tenantFromJwt = typeof user.app_metadata?.tenant_id === 'string'
      ? user.app_metadata.tenant_id
      : null;
    if (!tenantFromJwt) {
      console.warn('[AuthContext] tenant_id ausente no JWT — forçando relogin.');
      notifyError(
        'Sessão expirada',
        'Sua sessão precisa ser renovada. Entre novamente para continuar.',
      );
      void supabase.auth.signOut();
    }
  }, [bootstrap, user]);

  useEffect(() => {
    let initialLoadDone = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (import.meta.env.DEV) console.log(`[AuthContext] Evento: ${event}, path: ${locationRef.current}`);

        setSession(currentSession);
        const currentUser = currentSession?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          setLoading(false);
          initialLoadDone = true;

          // Redireciona ao painel apropriado quando o login vem da tela de login.
          // A escolha de destino respeita a hierarquia de papéis: admin sempre vai
          // ao dashboard global; demais perfis caem em seus portais dedicados.
          if (event === 'SIGNED_IN' && locationRef.current === '/login' && !locationRef.current.startsWith('/reset-password')) {
            // Se veio de um fluxo externo (ex.: /login?next=/.lovable/oauth/consent?...)
            // respeitar o destino, desde que seja same-origin relativo.
            const searchParams = new URLSearchParams(window.location.search);
            const nextParam = searchParams.get('next');
            const nextSafe =
              nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null;

            if (nextSafe) {
              if (import.meta.env.DEV) console.log(`[AuthContext] Redirecionando para next=${nextSafe}`);
              navigate(nextSafe, { replace: true });
              return;
            }

            void queryClient
              .prefetchQuery({
                queryKey: [...BOOTSTRAP_QUERY_KEY, currentUser.id],
                queryFn: async () => {
                  const { data, error } = await supabase.rpc('fn_bootstrap_usuario');
                  if (error) throw error;
                  return data;
                },
              })
              .finally(() => {
                const bootstrapData = queryClient.getQueryData<{ roles?: string[] }>([
                  ...BOOTSTRAP_QUERY_KEY,
                  currentUser.id,
                ]);
                const papeis = bootstrapData?.roles ?? [];
                let destino = '/dashboard';
                if (!papeis.includes('admin')) {
                  if (papeis.includes('coordenacao')) destino = '/portal/coordenacao';
                  else if (papeis.includes('professor')) destino = '/portal/professor';
                  else if (papeis.includes('aluno')) destino = '/portal/aluno';
                }
                if (import.meta.env.DEV) console.log(`[AuthContext] Redirecionando para ${destino}…`);
                navigate(destino, { replace: true });
              });
          }

        } else {
          // Limpa caches sensíveis no logout
          queryClient.removeQueries({ queryKey: BOOTSTRAP_QUERY_KEY });
          setLoading(false);
          initialLoadDone = true;

          if (event === 'SIGNED_OUT') {
            navigate('/login', { replace: true });
          }
        }
      }
    );

    const timeout = setTimeout(() => {
      if (!initialLoadDone) {
        setLoading(false);
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate, queryClient]);

  const hasRole = useCallback((role: AppRole) => checkRole(roles, role), [roles]);
  const hasAnyRole = useCallback((requiredRoles: AppRole[]) => checkAnyRole(roles, requiredRoles), [roles]);

  const signOut = async () => {
    setLoading(true);
    queryClient.removeQueries({ queryKey: BOOTSTRAP_QUERY_KEY });
    await supabase.auth.signOut();
    setLoading(false);
  };

  const value = useMemo<AuthContextType>(() => ({
    session, user, profile, roles, loading, tenantId,
    rolesLoading, rolesError, podeAcessar,
    hasRole, hasAnyRole, signOut, refreshProfile,
  }), [session, user, profile, roles, loading, tenantId, rolesLoading, rolesError, podeAcessar, hasRole, hasAnyRole, refreshProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
